import { BaseElement } from 'base';
import { MediaFloor, MEDIA_PLAYING_TOPIC } from 'media-floor';

const GRID = '(min-width: 1200px)';
const REDUCE = '(prefers-reduced-motion: reduce)';
const TOGGLE_TAG = 'action-button';
const PAUSED = 'paused';
const PLAYING = 'playing';
const STATES = [PAUSED, PLAYING];
const MAX_START_WAITS = 3;
const MAX_HOLD_WAITS = 1;
const PRELOAD_MARGIN = '100% 0px';
const FOCUS_VISIBLE = ':focus-visible';
const SUPPORTS_FOCUS_VISIBLE = CSS.supports(`selector(${FOCUS_VISIBLE})`);

class CommunityGrid extends BaseElement {
  #destroyed = false;
  #gridMq = null;
  #reduceMq = null;
  #track = null;
  #carouselToggle = null;
  #tiles = [];
  #segments = [];
  #videos = new Map();
  #toggles = new Map();
  #videoTiles = new Set();
  #stalled = new Set();
  #pending = new Set();
  #startWaits = new Map();
  #holdWaits = new Map();
  #attempts = new Map();
  #warmQueue = [];
  #warming = null;
  #ids = [];
  #activeIndex = 0;
  #floor = new MediaFloor();
  #offScreen = true;
  #userPaused = false;
  #reduceMotion = false;
  #externallyBlocked = false;
  #approached = false;
  #preloaded = false;

  onConnect() {
    this.#track = this.q('[data-track]');
    if (!this.#track) return;

    this.#destroyed = false;
    this.#gridMq = window.matchMedia(GRID);
    this.#reduceMq = window.matchMedia(REDUCE);
    this.#carouselToggle = this.q(
      `${TOGGLE_TAG}.community-grid__toggle--carousel`
    );
    this.#tiles = this.qa('[data-tile]');
    this.#segments = this.qa('[data-progress] .community-grid__segment');
    this.#ids = this.qa('[data-media-id]').map(tile => tile.dataset.mediaId);

    this.#videos = new Map();
    for (const video of this.qa('[data-video]')) {
      this.#videos.set(this.#tileOf(video), video);
    }
    this.#videoTiles = new Set(this.#videos.keys());
    this.#stalled = new Set();
    this.#pending = new Set();
    this.#startWaits = new Map();
    this.#holdWaits = new Map();
    this.#attempts = new Map();
    this.#warmQueue = [];
    this.#warming = null;

    this.#toggles = new Map();
    for (const toggle of this.qa(
      `${TOGGLE_TAG}.community-grid__toggle--tile`
    )) {
      this.#toggles.set(this.#tileOf(toggle), toggle);
    }

    this.#offScreen = true;
    this.#approached = false;
    this.#preloaded = false;
    this.#reduceMotion = this.#reduceMq.matches;
    this.#activeIndex = 0;

    this.subscribe(MEDIA_PLAYING_TOPIC, this.#onPlayingChanged);
    this.listen(this, 'animationend', this.#onSegmentEnd);
    this.listen(this, 'click', this.#onCardTap);
    this.listen(this.#track, 'focus', this.#onTrackFocus);
    this.listen(this.#gridMq, 'change', this.#applyTier);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);

    for (const video of this.#videos.values()) {
      this.listen(video, 'error', this.#onMediaError, true);
      this.listen(video, 'ended', this.#onEnded);
      this.listen(video, 'playing', this.#onVideoPlaying);
      this.listen(video, 'pause', this.#onVideoPause);
      this.listen(video, 'canplay', this.#onWarmSettled);
      this.listen(video, 'stalled', this.#onWarmSettled);
      this.listen(video, 'suspend', this.#onWarmSettled);
    }

    for (const [tile, video] of this.#videos) {
      if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        this.#degrade(tile);
      }
    }

    this.observe(
      new IntersectionObserver(this.#onSectionApproaching.bind(this), {
        rootMargin: PRELOAD_MARGIN
      }),
      this
    );

    this.observe(
      new IntersectionObserver(this.#onSectionVisible.bind(this), {
        threshold: 0.25
      }),
      this
    );

    const slides = new IntersectionObserver(this.#onSlideVisible.bind(this), {
      root: this.#track,
      threshold: 0.6
    });
    for (const tile of this.#tiles) this.observe(slides, tile);

    customElements.whenDefined(TOGGLE_TAG).then(() => this.#bindToggles());

    this.#applyTier();
  }

  onDisconnect() {
    this.#destroyed = true;
    this.#pauseOtherVideos();
    this.#releaseFloor();
  }

  #bindToggles() {
    if (this.#destroyed || !this.isConnected) return;

    for (const [tile, toggle] of this.#toggles) {
      toggle.toggleIcon({
        states: STATES,
        onChange: () => this.#toggleTile(tile)
      });
      this.#setToggleState(toggle, !this.#videos.get(tile).paused);
    }

    if (!this.#carouselToggle) return;
    this.#carouselToggle.toggleIcon({
      states: STATES,
      onChange: () => this.#toggleCarousel()
    });
    this.#setToggleState(this.#carouselToggle, !this.#paused);
  }

  #onPlayingChanged(id) {
    this.#externallyBlocked = Boolean(id) && !this.#ids.includes(id);
    if (!this.#externallyBlocked) return;
    this.#pauseOtherVideos();
    if (this.#gridMq.matches) return;

    const tile = this.#activeTile;
    if (!tile || tile.dataset.mediaStarted === undefined) return;
    this.#clearSlideMedia(tile);
    this.#restartSegment(tile);
  }

  #releaseFloor(tile) {
    this.#floor.release(tile?.dataset.mediaId);
  }

  #clearSlideMedia(tile) {
    if (!tile) return;
    delete tile.dataset.mediaStarted;
    this.#segmentFor(tile)?.style.removeProperty(
      '--community-grid-slide-duration'
    );
  }

  #toggleTile(tile) {
    const video = this.#videos.get(tile);
    if (!video) return;
    if (video.paused) {
      this.#startVideo(tile);
    } else {
      video.pause();
      this.#releaseFloor(tile);
    }
    this.#setToggleState(this.#toggles.get(tile), !video.paused);
  }

  #toggleCarousel() {
    this.#userPaused = !this.#userPaused;
    this.#syncPaused();
  }

  #onCardTap(event) {
    if (this.#gridMq.matches || this.#reduceMotion) return;
    if (event.target.closest(TOGGLE_TAG)) return;
    if (!this.#tileOf(event.target)) return;
    this.#toggleCarousel();
  }

  #onTrackFocus() {
    if (this.#gridMq.matches || this.#userPaused) return;
    if (SUPPORTS_FOCUS_VISIBLE && !this.#track.matches(FOCUS_VISIBLE)) return;
    this.#userPaused = true;
    this.#syncPaused();
  }

  #onVideoPlaying(event) {
    const video = event.target;
    const tile = this.#tileOf(video);
    if (!this.#videoTiles.has(tile)) return;
    tile.dataset.mediaStarted = '';
    this.#startWaits.delete(tile);
    this.#holdWaits.delete(tile);
    this.#setToggleState(this.#toggles.get(tile), true);

    const segment = this.#segmentFor(tile);
    if (!segment) return;
    if (segment.style.getPropertyValue('--community-grid-slide-duration')) {
      return;
    }
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    segment.style.setProperty(
      '--community-grid-slide-duration',
      `${video.duration}s`
    );
    this.#restartSegment(tile);
  }

  #onVideoPause(event) {
    this.#setToggleState(this.#toggles.get(this.#tileOf(event.target)), false);
  }

  #setToggleState(toggle, playing) {
    toggle?.setIconState?.(playing ? PLAYING : PAUSED);
  }

  #onEnded(event) {
    const tile = this.#tileOf(event.target);
    if (!this.#videoTiles.has(tile)) return;
    if (this.#gridMq.matches) {
      event.target.currentTime = 0;
      this.#setToggleState(this.#toggles.get(tile), true);
      this.#startVideo(tile);
      return;
    }
    this.#advance();
  }

  #onMediaError(event) {
    const tile = this.#tileOf(event.target);
    if (!tile) return;
    const video = this.#videos.get(tile);
    if (
      event.target.tagName === 'SOURCE' &&
      video?.networkState !== HTMLMediaElement.NETWORK_NO_SOURCE
    ) {
      return;
    }
    console.error('[community-grid] video failed to load:', event);
    this.#degrade(tile);
  }

  #supersedeAttempt(tile) {
    const attempt = (this.#attempts.get(tile) ?? 0) + 1;
    this.#attempts.set(tile, attempt);
    return attempt;
  }

  #isStale(tile, attempt) {
    return this.#attempts.get(tile) !== attempt;
  }

  #onPlayStarted(tile, attempt) {
    if (this.#isStale(tile, attempt)) return;
    this.#pending.delete(tile);
  }

  #onPlayRejected(error, tile, attempt) {
    if (this.#isStale(tile, attempt)) return;
    this.#pending.delete(tile);
    if (!this.isConnected || error.name === 'AbortError') return;
    this.#setToggleState(this.#toggles.get(tile), false);
    if (error.name === 'NotAllowedError') {
      this.#fallbackToPhotoCadence(tile);
      return;
    }
    console.error('[community-grid] playback failed:', error);
    this.#degrade(tile);
  }

  #fallbackToPhotoCadence(tile) {
    this.#clearSlideMedia(tile);
    this.#releaseFloor(tile);
  }

  #degrade(tile, recoverable = false) {
    this.#videos.get(tile)?.pause();
    this.#setToggleState(this.#toggles.get(tile), false);
    tile.dataset.mediaFailed = 'true';
    this.#pending.delete(tile);
    this.#startWaits.delete(tile);
    this.#holdWaits.delete(tile);
    this.#supersedeAttempt(tile);
    this.#clearSlideMedia(tile);
    this.#videoTiles.delete(tile);
    if (recoverable) {
      this.#stalled.add(tile);
      if (!this.#warmQueue.includes(tile)) this.#warmQueue.push(tile);
    } else {
      this.#stalled.delete(tile);
    }
    this.#releaseWarm(tile);
    this.#releaseFloor(tile);
    if (tile === this.#activeTile) {
      this.#restartSegment(tile);
      this.#syncPaused();
    }
  }

  #onSectionApproaching(entries) {
    if (!entries.some(entry => entry.isIntersecting)) return;
    this.#approached = true;
    this.#preloadVideos();
  }

  #onSectionVisible(entries) {
    this.#offScreen = !entries.at(-1).isIntersecting;
    this.#syncPaused();
  }

  #preloadVideos() {
    if (this.#preloaded || !this.#approached || this.#gridMq.matches) return;
    this.#preloaded = true;
    const count = this.#tiles.length;
    const reach = tile =>
      (this.#tiles.indexOf(tile) - this.#activeIndex + count) % count;
    this.#warmQueue = this.#tiles
      .filter(tile => this.#warmable(tile))
      .sort((first, second) => reach(first) - reach(second));
    this.#warmNext();
  }

  #warmable(tile) {
    return this.#videoTiles.has(tile) || this.#stalled.has(tile);
  }

  #warmNext() {
    if (this.#destroyed || !this.isConnected) return;
    let rotations = this.#warmQueue.length;
    while (this.#warmQueue.length) {
      const tile = this.#warmQueue.shift();
      const video = this.#videos.get(tile);
      if (!video || !this.#warmable(tile)) continue;
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) continue;
      if (
        this.#pending.has(tile) ||
        tile === this.#activeTile ||
        !video.paused
      ) {
        this.#warmQueue.push(tile);
        if (--rotations <= 0) return;
        continue;
      }
      this.#warming = tile;
      video.preload = 'auto';
      video.load();
      return;
    }
  }

  #releaseWarm(tile) {
    if (tile && this.#warming === tile) this.#warming = null;
    if (this.#warming) return;
    this.#warmNext();
  }

  #promoteWarm(tile) {
    if (this.#warming === tile) return;
    const queued = this.#warmQueue.indexOf(tile);
    if (queued >= 0) this.#warmQueue.splice(queued, 1);
    this.#warmQueue.unshift(tile);
    if (!this.#warming) this.#warmNext();
  }

  #onWarmSettled(event) {
    const tile = this.#tileOf(event.target);
    this.#reviveStalled(tile);
    this.#releaseWarm(tile);
  }

  #reviveStalled(tile) {
    if (!this.#stalled.has(tile)) return;
    const video = this.#videos.get(tile);
    if (!video || video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;
    this.#stalled.delete(tile);
    this.#videoTiles.add(tile);
    delete tile.dataset.mediaFailed;
  }

  #onReduceChange(event) {
    this.#reduceMotion = event.matches;
    this.#syncPaused();
  }

  #onSlideVisible(entries) {
    if (this.#gridMq.matches) return;
    const hit = entries.find(entry => entry.isIntersecting);
    if (hit) this.#setActive(this.#tiles.indexOf(hit.target));
  }

  #setActive(index) {
    if (index < 0 || index === this.#activeIndex) return;
    this.toggleAttribute('data-holding', false);
    this.#holdWaits.clear();
    this.#resetTile(this.#activeTile);
    this.#activeIndex = index;
    this.#releaseWarm(this.#activeTile);
    this.#stampSegments();
    this.#syncPaused();
  }

  #resetTile(tile) {
    if (!tile || !this.#videoTiles.has(tile)) return;
    const video = this.#videos.get(tile);
    video.pause();
    video.currentTime = 0;
    this.#clearSlideMedia(tile);
    this.#releaseFloor(tile);
  }

  #syncPaused() {
    const paused = this.#paused;
    this.toggleAttribute('data-paused', paused || this.#gridMq.matches);
    this.toggleAttribute(
      'data-user-paused',
      this.#userPaused && !this.#gridMq.matches
    );
    this.#setToggleState(this.#carouselToggle, !paused);

    if (paused) {
      this.#pauseOtherVideos();
      this.#releaseFloor();
      return;
    }
    if (this.#gridMq.matches) return;
    this.#playActiveVideo();
  }

  #playActiveVideo() {
    if (this.#destroyed || !this.isConnected || this.#externallyBlocked) return;
    const tile = this.#activeTile;
    this.#reviveStalled(tile);
    if (!this.#videoTiles.has(tile)) return;
    this.#startVideo(tile);
  }

  #startVideo(tile) {
    if (this.#destroyed || !this.isConnected) return;
    const video = this.#videos.get(tile);
    if (!video) return;
    if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
      this.#degrade(tile);
      return;
    }
    this.#pauseOtherVideos(tile);
    this.#floor.claim(tile.dataset.mediaId);
    this.#pending.add(tile);
    const attempt = this.#supersedeAttempt(tile);
    video.play().then(
      () => this.#onPlayStarted(tile, attempt),
      error => this.#onPlayRejected(error, tile, attempt)
    );
  }

  #pauseOtherVideos(keep) {
    for (const [tile, video] of this.#videos) {
      if (tile !== keep) video.pause();
    }
  }

  #onSegmentEnd(event) {
    if (event.animationName !== 'community-grid-progress') return;
    if (this.#segments.indexOf(event.target) !== this.#activeIndex) return;
    const tile = this.#activeTile;
    if (!tile) return;
    if (this.#videoTiles.has(tile)) {
      if (tile.dataset.mediaStarted !== undefined) return;
      if (this.#pending.has(tile)) {
        if (this.#awaitStart(tile)) {
          this.#hold(tile);
        } else {
          this.#advance();
        }
        return;
      }
    }
    if (this.#holdForNext()) {
      this.#hold(tile);
      return;
    }
    this.#advance();
  }

  #hold(tile) {
    this.#restartSegment(tile);
    this.toggleAttribute('data-holding', true);
  }

  #holdForNext() {
    if (this.#gridMq.matches) return false;
    const count = this.#tiles.length;
    if (count < 2) return false;
    const next = this.#tiles[(this.#activeIndex + 1) % count];
    if (!this.#videoTiles.has(next)) return false;
    const video = this.#videos.get(next);
    if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) return false;
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return false;
    const waits = (this.#holdWaits.get(next) ?? 0) + 1;
    if (waits > MAX_HOLD_WAITS) return false;
    this.#holdWaits.set(next, waits);
    this.#promoteWarm(next);
    return true;
  }

  #awaitStart(tile) {
    const video = this.#videos.get(tile);
    if (video?.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
      this.#degrade(tile);
      return false;
    }
    const waits = (this.#startWaits.get(tile) ?? 0) + 1;
    if (waits > MAX_START_WAITS) {
      this.#degrade(tile, true);
      return false;
    }
    this.#startWaits.set(tile, waits);
    return true;
  }

  #advance() {
    const count = this.#tiles.length;
    if (count === 0) return;
    if (count === 1) {
      this.#resetTile(this.#tiles[0]);
      this.#restartSegment(this.#tiles[0]);
      this.#syncPaused();
      return;
    }
    const next = (this.#activeIndex + 1) % count;
    this.#scrollToTile(next, next === 0);
  }

  #scrollToTile(index, instant) {
    const tile = this.#tiles[index];
    if (!tile) return;
    const left =
      tile.getBoundingClientRect().left -
      this.#track.getBoundingClientRect().left +
      this.#track.scrollLeft;
    this.#track.scrollTo({
      left,
      behavior: instant || this.#reduceMotion ? 'auto' : 'smooth'
    });
  }

  #applyTier() {
    this.#pauseOtherVideos();
    this.#releaseFloor();
    this.#clearSlides();

    if (this.#gridMq.matches) {
      this.#track.removeAttribute('tabindex');
      this.#userPaused = false;
      this.removeAttribute('data-user-paused');
      this.toggleAttribute('data-paused', true);
      return;
    }

    this.#track.setAttribute('tabindex', '0');
    this.#track.scrollTo({ left: 0, behavior: 'auto' });
    this.#activeIndex = 0;
    this.#preloadVideos();
    if (!this.#warming) this.#warmNext();
    this.#stampSegments();
    this.#syncPaused();
  }

  #stampSegments() {
    this.#segments.forEach((segment, index) => {
      if (index < this.#activeIndex) {
        segment.dataset.state = 'done';
        return;
      }
      if (index === this.#activeIndex) {
        segment.dataset.state = 'active';
        return;
      }
      segment.removeAttribute('data-state');
    });
  }

  #clearSlides() {
    this.toggleAttribute('data-holding', false);
    this.#holdWaits.clear();
    for (const tile of this.#tiles) this.#clearSlideMedia(tile);
    for (const segment of this.#segments) {
      segment.removeAttribute('data-state');
    }
  }

  #restartSegment(tile) {
    const segment = this.#segmentFor(tile);
    if (!segment) return;
    this.toggleAttribute('data-holding', false);
    segment.removeAttribute('data-state');
    void segment.offsetWidth;
    segment.dataset.state = 'active';
  }

  #segmentFor(tile) {
    return this.#segments[this.#tiles.indexOf(tile)];
  }

  #tileOf(node) {
    return node.closest('[data-tile]');
  }

  get #paused() {
    return this.#userPaused || this.#offScreen || this.#reduceMotion;
  }

  get #activeTile() {
    return this.#tiles[this.#activeIndex];
  }
}

if (!customElements.get('community-grid')) {
  customElements.define('community-grid', CommunityGrid);
}
